import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperclip, faDownload, faFilePdf, faFileWord, faFileExcel, faFilePowerpoint,
  faFileImage, faFileArchive, faFileLines, faFile,
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { SERVER_URL } from '../../../databucket';

const iconFor = (contentType = '', name = '') => {
  const ct = contentType.toLowerCase();
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (ct.includes('pdf') || ext === 'pdf') return { icon: faFilePdf, color: '#D93025' };
  if (ct.includes('word') || ['doc', 'docx'].includes(ext)) return { icon: faFileWord, color: '#1A73E8' };
  if (ct.includes('excel') || ct.includes('spreadsheet') || ['xls', 'xlsx', 'csv'].includes(ext)) return { icon: faFileExcel, color: '#188038' };
  if (ct.includes('presentation') || ['ppt', 'pptx'].includes(ext)) return { icon: faFilePowerpoint, color: '#EA4335' };
  if (ct.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) return { icon: faFileImage, color: '#8B5CF6' };
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return { icon: faFileArchive, color: '#F59E0B' };
  if (ct.startsWith('text/') || ['txt', 'log', 'md'].includes(ext)) return { icon: faFileLines, color: '#6B7280' };
  return { icon: faFile, color: '#6B7280' };
};

const formatSize = (bytes) => {
  if (!bytes || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Save a base64-encoded blob as a download
const saveBase64AsFile = (base64, name, contentType) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: contentType || 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name || 'attachment';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const downloadAttachment = async (att) => {
  try {
    // Graph attachments arrive with contentBytes inline — save directly
    if (att?.contentBytes) {
      saveBase64AsFile(att.contentBytes, att.name, att.contentType);
      return;
    }
    // SES attachments with a persisted S3 copy — fetch via the new backend endpoint then save
    if (att?.s3Key && att?.trackingId !== undefined && att?.index !== undefined) {
      const { data } = await axios.get(`${SERVER_URL}/api/ses/attachment`, {
        params: { trackingId: att.trackingId, index: att.index },
      });
      if (data?.contentBytes) {
        saveBase64AsFile(data.contentBytes, data.name || att.name, data.contentType || att.contentType);
      }
    }
  } catch (e) {
    console.error('Attachment download error:', e);
  }
};

const AttachmentList = ({ items = [], loading = false }) => {
  // Filter out inline attachments (typically images embedded in body)
  const visible = items.filter(a => !a.isInline);
  if (loading) {
    return (
      <div style={styles.wrapper}>
        <div style={styles.label}>
          <FontAwesomeIcon icon={faPaperclip} style={{ marginRight: 6 }} />
          Loading attachments…
        </div>
      </div>
    );
  }
  if (visible.length === 0) return null;
  return (
    <div style={styles.wrapper}>
      <div style={styles.label}>
        <FontAwesomeIcon icon={faPaperclip} style={{ marginRight: 6 }} />
        {visible.length} attachment{visible.length !== 1 ? 's' : ''}
      </div>
      <div style={styles.row}>
        {visible.map((a) => {
          const { icon, color } = iconFor(a.contentType, a.name);
          // Downloadable when bytes are inline (Graph) OR when a persisted S3 copy exists (SES)
          const downloadable = !!a.contentBytes || !!a.s3Key;
          return (
            <div key={a.id} style={styles.card} title={a.name}>
              <FontAwesomeIcon icon={icon} style={{ color, fontSize: 22, flexShrink: 0 }} />
              <div style={styles.info}>
                <div style={styles.name}>{a.name}</div>
                <div style={styles.meta}>{formatSize(a.size)}</div>
              </div>
              <button
                style={{ ...styles.downloadBtn, opacity: downloadable ? 1 : 0.4, cursor: downloadable ? 'pointer' : 'not-allowed' }}
                onClick={() => downloadable && downloadAttachment(a)}
                disabled={!downloadable}
                title={downloadable ? 'Download' : 'Original file not stored — only metadata is available for this email'}
              >
                <FontAwesomeIcon icon={faDownload} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const styles = {
  wrapper: { padding: '12px 24px', borderTop: '1px solid #edebe9', borderBottom: '1px solid #edebe9', background: '#faf9f8' },
  label: { fontSize: 12, fontWeight: 600, color: '#605e5c', marginBottom: 8 },
  row: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  card: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#fff', border: '1px solid #edebe9', borderRadius: 6, minWidth: 220, maxWidth: 320 },
  info: { flex: 1, minWidth: 0 },
  name: { fontSize: 13, fontWeight: 500, color: '#201f1e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  meta: { fontSize: 11, color: '#797775', marginTop: 2 },
  downloadBtn: { background: 'transparent', border: '1px solid #d2d0ce', borderRadius: 4, padding: '4px 8px', color: '#0078d4', fontSize: 12 },
};

export default AttachmentList;
